import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PullRow } from '@/lib/pull-through';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';

/**
 * WHAT HAS TO COME WITH A REFERENCE.
 *
 * A chip names an answer in another task. Pasted into an experience that has no such task, it
 * can never resolve — the block goes red and the teacher is left to work out what it used to
 * point at. This asks whether to bring those tasks too.
 *
 * Structured after `PasteConflictDialog`, which answers the same shape of question for a
 * scenario set, and borrows its two hard-won decisions:
 *
 *  - the checklist is ALWAYS OPEN, because it is the only thing that says what is actually
 *    about to be added to the experience, and a gut check behind a disclosure is one nobody
 *    takes;
 *  - presets first, for the two common answers, so the teacher who wants "all of it" does not
 *    tick six rows to get there.
 *
 * It follows that dialog's silence rule too, one level up: a reference whose task is ALREADY in
 * the destination needs nothing pulled, so this never opens. Same-document pastes — every
 * Duplicate — therefore behave exactly as they did before it existed.
 */
export default function PullThroughDialog({ rows, onCancel, onSkip, onConfirm }: {
  /** Shallowest first: what the copy itself cites, then what those drag in. */
  rows: PullRow[];
  /*
   * THREE OUTCOMES, because the dialog stands in front of a paste the teacher already asked
   * for. Dismissing it has to mean "I have changed my mind about the paste" and not silently
   * commit the version of it they were being asked about.
   */
  /** Abandon the paste entirely — the X, and Escape. */
  onCancel: () => void;
  /** Paste as it would have pasted before this existed: named, broken, repointable. */
  onSkip: () => void;
  /** Paste, bringing these tasks in front of it. Never empty — that is `onSkip`. */
  onConfirm: (taskIds: string[]) => void;
}) {
  const { t } = useTranslation();

  // Everything, by default. The reason the dialog is open at all is that these are needed.
  const [include, setInclude] = useState<Set<string>>(() => new Set(rows.map(r => r.taskId)));
  // The tail only exists when something is cited at one remove, so the preset for trimming it
  // only exists then either.
  const direct = rows.filter(r => r.depth === 0).map(r => r.taskId);
  const hasChain = direct.length !== rows.length;

  const toggle = (id: string) => setInclude(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <ModalShell
      title={t('task.paste.pull.title')}
      onClose={onCancel}
      maxWidth='max-w-lg'
      fullBleedOnPhone
    >
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-ws-muted'>{t('task.paste.pull.intro', { count: rows.length })}</p>

        {hasChain && (
          <div className='flex flex-wrap gap-2'>
            <Button size='sm' pressed={include.size === rows.length}
              onClick={() => setInclude(new Set(rows.map(r => r.taskId)))}>
              {t('task.paste.pull.all', { count: rows.length })}
            </Button>
            {/* "Only what I am pasting cites" — the rest arrive broken, which is the teacher's
                call to make, not ours to prevent. */}
            <Button size='sm' pressed={include.size === direct.length && direct.every(id => include.has(id))}
              onClick={() => setInclude(new Set(direct))}>
              {t('task.paste.pull.directOnly', { count: direct.length })}
            </Button>
          </div>
        )}

        <div className='rounded-xl border border-ws-border p-3'>
          <div className='flex flex-col'>
            {rows.map(row => (
              <CheckboxRow
                key={row.taskId}
                checked={include.has(row.taskId)}
                onToggle={() => toggle(row.taskId)}
                /*
                 * Why it is here, on the row, because the answer differs per row and a single
                 * line of preamble cannot say which task needed which.
                 *
                 * A DRIVING row says so, because declining it costs more. A chip that cannot
                 * resolve leaves a visible hole in one sentence; a block that loses its driver
                 * keeps every authored version with nothing to choose between them, and the
                 * teacher has to reconnect it or throw the lot away.
                 */
                label={row.citedBy.length > 0
                  ? t(row.drives ? 'task.paste.pull.rowCitedDriving' : 'task.paste.pull.rowCited',
                    { title: row.title, cites: row.citedBy.join(', ') })
                  : row.drives
                    ? t('task.paste.pull.rowDriving', { title: row.title })
                    : row.title}
              />
            ))}
          </div>
        </div>

        {/* Pinned below the list, so the way out is reachable however many rows there are. */}
        <div className='sticky bottom-0 flex justify-end gap-2 bg-ws-elevated pt-2'>
          <Button variant='secondary' onClick={onSkip}>{t('task.paste.pull.skip')}</Button>
          {/* Unticking everything IS skipping, so the primary says so rather than offering to
              bring nothing — a button whose label counts to zero is one nobody trusts. */}
          {include.size === 0 ? (
            <Button variant='primary' onClick={onSkip}>{t('task.paste.pull.skip')}</Button>
          ) : (
            <Button variant='primary' onClick={() => onConfirm(rows.filter(r => include.has(r.taskId)).map(r => r.taskId))}>
              {t('task.paste.pull.confirm', { count: include.size })}
            </Button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
